<template>
    <span
        class="activix-radio"
        :class="radioClasses"
        data-role="activix-radio-item"
        role="radio"
        :aria-checked="isChecked.toString()"
        :aria-disabled="isDisabled.toString()"
        :tabindex="tabIndex"
        @keydown.space.stop.prevent="handleSpaceKey"
        @click="handleClick"
    >
        <span class="activix-radio__input w-3.5 h-3.5" :class="inputClasses">
            <span
                class="activix-radio__inner w-3.5 h-3.5 block relative bg-white border border-gray-300 rounded-full transition-colors duration-300"
            />
            <input
                class="activix-radio__original"
                type="radio"
                :value="label"
                :name="radioName"
                :disabled="isDisabled"
                tabindex="-1"
                autocomplete="off"
                aria-hidden="true"
                ref="radio"
                @focus="onFocus"
                @blur="onBlur"
                @change="handleChange"
                v-model="model"
            />
        </span>
        <span class="activix-radio__label">
            <slot />
            <template v-if="!$slots.default">{{ label }}</template>
        </span>
    </span>
</template>

<script setup lang="ts">
    import { computed, ref, inject } from 'vue';
    import type { RadioGroupContext } from './types.ts';
    import { RadioSize } from './types.ts';

    interface Props {
        value?: any;
        label?: any;
        disabled?: boolean;
        name?: string;
        border?: boolean;
        size?: RadioSize;
    }

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        border: false,
    });

    const emit = defineEmits<{
        (e: 'input', value: any): void;
        (e: 'change', value: any): void;
    }>();

    const focus = ref(false);
    const radio = ref<HTMLInputElement | null>(null);

    // Check if inside RadioGroup
    const radioGroup = inject<RadioGroupContext | null>('radioGroup', null);
    const isGroup = computed(() => radioGroup !== null);

    // Model value (from group or standalone)
    const model = computed({
        get() {
            return isGroup.value ? radioGroup!.value.value : props.value;
        },
        set(val: any) {
            if (isGroup.value && radioGroup?.changeValue) {
                // Call group's changeValue method to update v-model
                radioGroup.changeValue(val);
            } else {
                emit('input', val);
            }
            // Update checked state
            if (radio.value) {
                radio.value.checked = model.value === props.label;
            }
        },
    });

    // Size (from group or prop)
    const radioSize = computed(() => {
        return isGroup.value ? radioGroup!.size : props.size;
    });

    // Disabled (from group or prop)
    const isDisabled = computed(() => {
        return isGroup.value ? radioGroup!.disabled || props.disabled : props.disabled;
    });

    // Checked state
    const isChecked = computed(() => model.value === props.label);

    // Tab index for keyboard navigation
    const tabIndex = computed(() => {
        return isDisabled.value || (isGroup.value && !isChecked.value) ? -1 : 0;
    });

    // Radio name (from group or prop)
    const radioName = computed(() => {
        return isGroup.value ? radioGroup!.name : props.name;
    });

    // CSS classes
    const radioClasses = computed(() => [
        {
            'is-disabled': isDisabled.value,
            'is-focus': focus.value,
            'is-bordered': props.border,
            'is-checked': isChecked.value,
        },
        radioSize.value ? `activix-radio--${radioSize.value}` : '',
    ]);

    const inputClasses = computed(() => ({
        'is-disabled': isDisabled.value,
        'is-checked': isChecked.value,
    }));

    // Event handlers
    const onFocus = () => {
        focus.value = true;
    };

    const onBlur = () => {
        focus.value = false;
    };

    const handleChange = () => {
        // nextTick equivalent - emit change after DOM updates
        setTimeout(() => {
            emit('change', model.value);
        }, 0);
    };

    const handleSpaceKey = () => {
        if (!isDisabled.value) {
            model.value = props.label;
        }
    };

    const handleClick = () => {
        if (!isDisabled.value) {
            model.value = props.label;
        }
    };
</script>

<script lang="ts">
    export default {
        name: 'ActivixRadio',
    };
</script>

<style scoped>
    .activix-radio {
        @apply inline-flex items-center cursor-pointer relative font-normal text-gray-700 mr-8;
    }

    .activix-radio.is-disabled {
        @apply cursor-not-allowed;
    }

    .activix-radio.is-bordered {
        @apply border border-gray-300 rounded px-5 py-3;
    }

    .activix-radio.is-bordered.is-checked {
        @apply border-blue-500;
    }

    .activix-radio.is-bordered.is-disabled {
        @apply border-gray-200 bg-gray-50;
    }

    .activix-radio--medium.is-bordered {
        @apply px-5 py-3;
    }

    .activix-radio--small.is-bordered {
        @apply px-4 py-2 text-sm;
    }

    .activix-radio--mini.is-bordered {
        @apply px-3 py-1 text-xs;
    }

    .activix-radio__input {
        @apply inline-flex items-center justify-center relative cursor-pointer;
    }

    .activix-radio__input.is-disabled {
        @apply cursor-not-allowed;
    }

    .activix-radio__input:hover .activix-radio__inner {
        @apply border-blue-500;
    }

    .activix-radio__input.is-checked .activix-radio__inner {
        @apply border-blue-500 bg-blue-500;
    }

    .activix-radio__input.is-checked .activix-radio__inner::after {
        content: '';
        @apply absolute bg-white rounded-full;
        width: 4px;
        height: 4px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(1);
    }

    .activix-radio__input.is-disabled .activix-radio__inner {
        @apply bg-gray-100 border-gray-200 cursor-not-allowed;
    }

    .activix-radio__input.is-disabled.is-checked .activix-radio__inner {
        @apply bg-gray-200;
    }

    .activix-radio__original {
        @apply absolute opacity-0 outline-none;
        margin: 0;
        width: 0;
        height: 0;
        z-index: -1;
    }

    .activix-radio__label {
        @apply ml-2 text-lg;
    }

    .activix-radio.is-disabled .activix-radio__label {
        @apply text-gray-400 cursor-not-allowed;
    }

    .activix-radio--small .activix-radio__input,
    .activix-radio--small .activix-radio__inner {
        width: 12px;
        height: 12px;
    }

    .activix-radio--mini .activix-radio__input,
    .activix-radio--mini .activix-radio__inner {
        width: 10px;
        height: 10px;
    }

    .activix-radio--small .activix-radio__label {
        @apply text-xs;
    }

    .activix-radio--mini .activix-radio__label {
        @apply text-xs;
    }
</style>
